import { getPermalinkEncodedFilterKeys } from '@/features/seo-listing/consts';

export type PermalinkIdentityFilters = {
  selectedFandoms: string[];
  selectedCharacters: string[];
  selectedTags: string[];
  selectedPairingCharacters: string[];
};

const sameSingleId = (selected: string[], expected?: string[]): boolean => {
  if (!expected || expected.length === 0) return selected.length === 1;
  return selected.length === 1 && expected.length === 1 && selected[0] === expected[0];
};

const sameIdSet = (selected: string[], expected?: string[]): boolean => {
  if (!expected || expected.length === 0) return selected.length > 0;
  if (selected.length !== expected.length) return false;
  const left = [...selected].sort().join(',');
  const right = [...expected].sort().join(',');
  return left === right;
};

/**
 * True when the path-encoded identity of the current permalink is still
 * represented by filter state. Used so SEO 404 for an *extra* facet
 * (characters on a fandom page) keeps the permanent URL + GET pass-through,
 * while clearing identity, swapping to a different entity, or multi-selecting
 * identity facets falls back off the stale permalink (DEV-490).
 *
 * When `expectedIdentity` is provided (remembered path identity), selected
 * ids must match — not only the count. Pairing pages encode two character ids.
 */
export const doesFilterStateMatchPermalinkIdentity = (
  pathname: string,
  filters: PermalinkIdentityFilters,
  expectedIdentity?: PermalinkIdentityFilters | null,
): boolean => {
  const encodedKeys = getPermalinkEncodedFilterKeys(pathname);
  if (
    encodedKeys.includes('fandoms') &&
    !sameSingleId(filters.selectedFandoms, expectedIdentity?.selectedFandoms)
  ) {
    return false;
  }
  if (
    encodedKeys.includes('characters') &&
    !sameSingleId(filters.selectedCharacters, expectedIdentity?.selectedCharacters)
  ) {
    return false;
  }
  if (
    encodedKeys.includes('tags') &&
    !sameSingleId(filters.selectedTags, expectedIdentity?.selectedTags)
  ) {
    return false;
  }
  if (
    encodedKeys.includes('pairingCharacters') &&
    !sameIdSet(filters.selectedPairingCharacters, expectedIdentity?.selectedPairingCharacters)
  ) {
    return false;
  }
  return true;
};
